import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { sortBy } from 'lodash';
import { toast } from 'react-toastify';

import * as Core from '../../../core';
import { Button } from '../../../components/button';
import { HollowButton, SolidButton } from '../../../components/buttons-visuals';
import Modal from '../../../components/overlays/modal/Modal';
import { MatchService } from '../../../services/matchService';

import './cs2DeferredMapSetup.scss';

const MAP_IMAGE_BASE_URL = 'https://raw.githubusercontent.com/MurkyYT/cs2-map-icons/main/images';
const MAP_THUMB_BASE_URL = `${MAP_IMAGE_BASE_URL}/thumbs`;

const normalizeMapSlug = (id: string) => id.trim().toLowerCase().replace(/\s+/g, '_');

const defaultThumbUrlForMap = (mapId: string) => {
    const slug = normalizeMapSlug(mapId);
    return `${MAP_THUMB_BASE_URL}/${slug}_1_png.png`;
};

const thumbUrlForRow = (row: Core.Models.Cs2CompetitiveMap) =>
    row.imageThumbnailUrl?.trim() || defaultThumbUrlForMap(row.mapId);

export const cs2MatchGameNeedsDeferredMapSetup = (matchGame: Core.Models.MatchGame): boolean => {
    const s = matchGame.titleIntegrationState;
    if (
        s === Core.Models.TitleIntegrationState.PendingConfiguration ||
        s === Core.Models.TitleIntegrationState.Failed
    )
        return true;
    if (s === undefined && !matchGame.titleMetadata) return true;
    return false;
};

interface Cs2DeferredMapSetupProps {
    matchId: string;
    matchGame: Core.Models.MatchGame;
    canApply: boolean;
    canReset: boolean;
    onUpdated: () => Promise<void>;
}

const Cs2DeferredMapSetup = ({
    matchId,
    matchGame,
    canApply,
    canReset,
    onUpdated,
}: Cs2DeferredMapSetupProps): JSX.Element => {
    const [maps, setMaps] = useState<Core.Models.Cs2CompetitiveMap[] | null>(null);
    const [mapsError, setMapsError] = useState<string | null>(null);
    const [selectedMapId, setSelectedMapId] = useState<string | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isResetting, setIsResetting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await MatchService.getCs2CompetitiveMaps();
                if (!cancelled) {
                    setMaps(sortBy(data, (m) => m.sortOrder));
                    setMapsError(null);
                }
            } catch (e) {
                if (!cancelled) {
                    setMapsError(Core.API.getErrorMessage(e));
                    setMaps([]);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const selectedMapLabel = useMemo(
        () => maps?.find((m) => m.mapId === selectedMapId)?.displayName,
        [maps, selectedMapId]
    );

    const openConfirm = useCallback((mapId: string) => {
        setSelectedMapId(mapId);
        setConfirmOpen(true);
    }, []);

    const submitMap = useCallback(async () => {
        if (!selectedMapId || isSubmitting) return;
        setIsSubmitting(true);
        try {
            await MatchService.applyMatchGameTitleSetup({
                matchId,
                matchGameId: matchGame.id,
                configuration: { map: selectedMapId },
            });
            toast.success('Map submitted — creating server…');
            setConfirmOpen(false);
            setSelectedMapId(null);
            await onUpdated();
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        } finally {
            setIsSubmitting(false);
        }
    }, [isSubmitting, matchGame.id, matchId, onUpdated, selectedMapId]);

    const resetSetup = useCallback(async () => {
        if (isResetting) return;
        if (!window.confirm('Reset CS2 server setup? This clears the current server so a new map can be chosen.'))
            return;
        setIsResetting(true);
        try {
            await MatchService.resetMatchGameTitleSetup({ matchId, matchGameId: matchGame.id });
            toast.success('Setup reset — select a map again.');
            await onUpdated();
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        } finally {
            setIsResetting(false);
        }
    }, [isResetting, matchGame.id, matchId, onUpdated]);

    return (
        <div className="cs2-deferred-map-setup mb2x">
            <p className="cs2-deferred-map-setup__intro">
                <strong>Game {matchGame.sortOrder} — choose the competitive map.</strong> Everyone on the match page will
                see updates when the server is ready.
            </p>
            {mapsError && (
                <p className="cs2-deferred-map-setup__error mb">Could not load map list: {mapsError}</p>
            )}
            {maps === null ? (
                <p className="cs2-deferred-map-setup__loading">Loading maps…</p>
            ) : maps.length === 0 ? (
                <p className="cs2-deferred-map-setup__error">No active competitive maps are configured.</p>
            ) : (
                <div className="cs2-deferred-map-setup__grid">
                    {maps.map((m) => (
                        <button
                            type="button"
                            key={m.id}
                            className="cs2-deferred-map-setup__tile"
                            disabled={!canApply}
                            onClick={() => openConfirm(m.mapId)}
                            title={canApply ? `Select ${m.displayName}` : 'You do not have permission to configure the map'}
                        >
                            <span
                                className="cs2-deferred-map-setup__tile-image"
                                style={{ backgroundImage: `url(${thumbUrlForRow(m)})` }}
                            />
                            <span className="cs2-deferred-map-setup__tile-label">{m.displayName}</span>
                        </button>
                    ))}
                </div>
            )}
            {canReset &&
                (matchGame.titleIntegrationState === Core.Models.TitleIntegrationState.Provisioned ||
                    matchGame.titleIntegrationState === Core.Models.TitleIntegrationState.Failed) && (
                <div className="cs2-deferred-map-setup__host-actions mt">
                    <HollowButton as="button" disabled={isResetting} onClick={() => void resetSetup()} size="medium">
                        {isResetting ? 'Resetting…' : 'Wrong map? Reset server setup (host only)'}
                    </HollowButton>
                </div>
            )}
            <Modal title="Confirm map" isOpen={confirmOpen} onClose={() => !isSubmitting && setConfirmOpen(false)}>
                <p className="mb2x">
                    Create the CS2 server on <strong>{selectedMapLabel}</strong>? This cannot be changed without a host
                    reset afterward.
                </p>
                <div className="disp-flex justify-end gap">
                    <Button
                        disabled={isSubmitting}
                        onClick={() => !isSubmitting && setConfirmOpen(false)}
                        semiRound
                        styleType={Core.Models.StyleType.Secondary}
                    >
                        Cancel
                    </Button>
                    <SolidButton as="button" disabled={isSubmitting} onClick={() => void submitMap()} size="medium">
                        {isSubmitting ? 'Working…' : 'Yes, use this map'}
                    </SolidButton>
                </div>
            </Modal>
        </div>
    );
};

export default Cs2DeferredMapSetup;
